import type { ApiClient, ApiResult } from './client'
import type {
	DayRange,
	Granularity,
	Listing,
	Locale,
	Locality,
	MagicPlace,
	MagicPlaceProposal,
	MagicPlaceStatus,
	Municipality,
	MunicipalityActivity,
	Page,
	Participation,
	PoiCategory,
	PointOfInterest,
	ProposalReceipt,
	Region,
	ReportReceipt,
	ReportRow,
	Species,
	SpeciesSummary,
	StaffSession,
	StaffUser,
	Stage,
	StageDetail,
	TreeCondition,
	TreeDetail,
	TreeHealth,
	TreeHistory,
	TreeStatusRow,
	TreeSummary,
} from './types'

// Endpoints públicos de la API /v1 que usa la webapp. Funciones puras sobre el cliente.

export type ReportInput = {
	condition: TreeCondition
	channel: 'qr' | 'map'
	anonymousId: string
	photo?: Blob | null
	location?: { latitude: number; longitude: number } | null
}

export type MagicPlaceInput = {
	name: string
	photo: Blob
	location: { latitude: number; longitude: number }
	anonymousId: string
}

// La foto viaja como File con nombre .jpg (la webapp la reduce a JPEG). El backend decide el
// formato por el contenido, pero un nombre coherente evita sorpresas en cualquier cliente.
export const asPhotoFile = (photo: Blob): File =>
	photo instanceof File && photo.name ? photo : new File([photo], 'foto.jpg', { type: photo.type || 'image/jpeg' })

export type PanelScope = { municipality?: string; stage?: string; species?: string }
export type PageRequest = { page?: number; limit?: number }
export type MagicPlaceFilter = PageRequest & { status?: MagicPlaceStatus }

export type ReportFilter = Partial<DayRange> &
	PageRequest & { condition?: TreeCondition; municipality?: string; stage?: string; tree?: string }

// Un listado paginado lleva la paginación en el sobre, no en `data`. Si faltase, es una sola página.
export const toPage = <T>({ data, meta }: ApiResult<T[]>): Page<T> => ({
	items: data,
	meta: meta ?? { total: data.length, page: 1, limit: data.length, pages: 1, hasMore: false },
})

export const createEndpoints = (client: ApiClient) => ({
	stages: (lang: Locale) => client.get<Listing<Stage>>('/v1/stages', { lang }).then((r) => r.data),
	stage: (id: string, lang: Locale) =>
		client.get<StageDetail>(`/v1/stages/${encodeURIComponent(id)}`, { lang }).then((r) => r.data),
	municipalities: (lang: Locale) =>
		client.get<Listing<Municipality>>('/v1/municipalities', { lang }).then((r) => r.data),
	localities: (lang: Locale) => client.get<Listing<Locality>>('/v1/localities', { lang }).then((r) => r.data),
	regions: (lang: Locale) => client.get<Listing<Region>>('/v1/regions', { lang }).then((r) => r.data),
	pointsOfInterest: (
		lang: Locale,
		filter: {
			stage?: string
			municipality?: string
			locality?: string
			category?: PoiCategory
			withPhotos?: boolean
		} = {}
	) =>
		client
			.get<Listing<PointOfInterest>>('/v1/points-of-interest', {
				lang,
				...filter,
				withPhotos: filter.withPhotos || undefined,
			})
			.then((r) => r.data),
	speciesList: (lang: Locale, region?: string) =>
		client.get<Listing<SpeciesSummary>>('/v1/species', { lang, region }).then((r) => r.data),
	species: (id: string, lang: Locale) =>
		client.get<Species & { locale: Locale }>(`/v1/species/${encodeURIComponent(id)}`, { lang }).then((r) => r.data),
	trees: (lang: Locale, filter: { stage?: string; municipality?: string } = {}) =>
		client.get<Listing<TreeSummary>>('/v1/trees', { lang, ...filter }).then((r) => r.data),
	tree: (code: string, lang: Locale) =>
		client.get<TreeDetail>(`/v1/trees/${encodeURIComponent(code)}`, { lang }).then((r) => r.data),

	reportTree: (code: string, input: ReportInput) => {
		const form = new FormData()
		form.append('condition', input.condition)
		form.append('channel', input.channel)
		form.append('anonymousId', input.anonymousId)
		if (input.photo) form.append('photo', asPhotoFile(input.photo))
		if (input.location) {
			form.append('latitude', String(input.location.latitude))
			form.append('longitude', String(input.location.longitude))
		}
		return client.postForm<ReportReceipt>(`/v1/trees/${encodeURIComponent(code)}/reports`, form).then((r) => r.data)
	},

	proposeMagicPlace: (input: MagicPlaceInput, lang: Locale) => {
		const form = new FormData()
		form.append('name', input.name)
		form.append('photo', asPhotoFile(input.photo))
		form.append('latitude', String(input.location.latitude))
		form.append('longitude', String(input.location.longitude))
		form.append('anonymousId', input.anonymousId)
		return client.postForm<ProposalReceipt>('/v1/magic-place-proposals', form, { lang }).then((r) => r.data)
	},

	// Panel de seguimiento: inicio de sesión del personal y lecturas autenticadas con su token.
	login: (email: string, password: string) =>
		client.postJson<StaffSession>('/v1/auth/session', { email, password }).then((r) => r.data),
	me: (token: string) => client.get<StaffUser>('/v1/auth/me', undefined, { token }).then((r) => r.data),
	treeHealth: (token: string, scope: PanelScope = {}) =>
		client.get<TreeHealth>('/v1/monitoring/tree-health', scope, { token }).then((r) => r.data),
	treesByCondition: (token: string, filter: PanelScope & PageRequest & { condition?: TreeCondition } = {}) =>
		client.get<TreeStatusRow[]>('/v1/monitoring/trees', filter, { token }).then(toPage),
	treeHistory: (token: string, code: string) =>
		client
			.get<TreeHistory>(`/v1/monitoring/trees/${encodeURIComponent(code)}/reports`, undefined, { token })
			.then((r) => r.data),
	activityByMunicipality: (token: string, range: DayRange, lang: Locale) =>
		client
			.get<MunicipalityActivity>('/v1/monitoring/activity-by-municipality', { ...range, lang }, { token })
			.then((r) => r.data),
	participation: (token: string, range: DayRange, granularity: Granularity) =>
		client
			.get<Participation>('/v1/monitoring/participation', { ...range, granularity }, { token })
			.then((r) => r.data),
	reports: (token: string, filter: ReportFilter = {}) =>
		client.get<ReportRow[]>('/v1/monitoring/reports', filter, { token }).then(toPage),

	// Lugares mágicos: los aprobados para todos; la moderación, para el personal.
	magicPlaces: () => client.get<{ items: MagicPlace[] }>('/v1/magic-places').then((r) => r.data.items),
	magicPlaceProposals: (token: string, filter: MagicPlaceFilter = {}) =>
		client.get<MagicPlaceProposal[]>('/v1/monitoring/magic-place-proposals', filter, { token }).then(toPage),
	magicPlacesOnMap: (token: string) =>
		client
			.get<{ items: MagicPlaceProposal[] }>('/v1/monitoring/magic-place-proposals/on-map', undefined, { token })
			.then((r) => r.data.items),
	approveMagicPlace: (token: string, id: string) =>
		client
			.send<MagicPlaceProposal>(
				'POST',
				`/v1/monitoring/magic-place-proposals/${encodeURIComponent(id)}/approve`,
				{
					token,
				}
			)
			.then((r) => r.data),
	retireMagicPlace: (token: string, id: string) =>
		client
			.send<MagicPlaceProposal>('POST', `/v1/monitoring/magic-place-proposals/${encodeURIComponent(id)}/retire`, {
				token,
			})
			.then((r) => r.data),
	deleteMagicPlace: (token: string, id: string) =>
		client
			.send<null>('DELETE', `/v1/monitoring/magic-place-proposals/${encodeURIComponent(id)}`, { token })
			.then(() => undefined),
})

export type Endpoints = ReturnType<typeof createEndpoints>
